var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import WebSocketAsPromised from "websocket-as-promised";
import { STR_ERRORS } from "../../Strings";
import { getFunction } from "../../Helpers";
import { stringify } from "query-string";
const TIME_LIMIT_FOR_OPENING_CONNECTION = 5000; // in miliseconds
export class AsyncSocket {
    constructor(options) {
        this.open = (x) => {
            this.handlers.open(x);
        };
        this.close = (x) => {
            this.handlers.close(x);
        };
        this.error = (x) => {
            this.handlers.error(x);
        };
        this.message = (x) => {
            this.handlers.message(this.handlers.responseFormatter(x));
        };
        this.url = options.url;
        const requestFormatter = (x) => JSON.stringify(x);
        this.handlers = {
            open: getFunction(options.handlers.open),
            close: getFunction(options.handlers.close),
            error: getFunction(options.handlers.error),
            message: getFunction(options.handlers.message),
            responseFormatter: getFunction(options.handlers.responseFormatter),
            requestFormatter: getFunction(options.handlers.requestFormatter, requestFormatter)
        };
    }
    // Mock server cannot accuretly represent the events needed to mock the behavior
    // described in the "isConnected" function, thus I am forced to ignore the lines below
    // from the code coverage tool. The business flows are covered with tests such as
    // expect(await x.isConnected()).toBe(y) for the different possibilities.
    /* istanbul ignore next */
    isConnected() {
        if (typeof this.socket === "undefined") {
            return Promise.resolve(false);
        }
        const startTime = Date.now();
        const socket = this.socket;
        return new Promise((resolve, reject) => {
            return (function pending() {
                if (socket.isClosing || socket.isClosed) {
                    return resolve(false);
                }
                if (socket.isOpened) {
                    return resolve(true);
                }
                // NOTE: The current websocket mock doesn't allow to test this timeout which happens on isOpening state
                // TODO: Find a better websocket mock server
                setTimeout(() => {
                    if (Date.now() - startTime > TIME_LIMIT_FOR_OPENING_CONNECTION) {
                        this.handlers.onClose({
                            code: 4001
                        });
                        return reject(STR_ERRORS.ReachedTimeLimitForOpeningConnection);
                    }
                    pending();
                }, 50);
            }());
        });
    }
    isConnectedSync() {
        var _a;
        return Boolean((_a = this.socket) === null || _a === void 0 ? void 0 : _a.isOpened);
    }
    deactivate() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            // TODO: websocket-as-promise does not implement
            // params for passing through on .close()
            // this way we are unable to pass custom code and signal
            // to be used for the reason for closing the socket.
            // Investigated opportunity to extend the library to support
            // params pass through - sent PR to the project:
            // https://github.com/vitalets/websocket-as-promised/pull/24
            // https://www.npmjs.com/package/websocket-as-promised
            yield ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.close());
            (_b = this.socket) === null || _b === void 0 ? void 0 : _b.removeAllListeners();
            this.socket = undefined;
            return yield this.isConnected();
        });
    }
    send(message) {
        var _a;
        return (_a = this.socket) === null || _a === void 0 ? void 0 : _a.send(this.handlers.requestFormatter(message));
    }
    connect(url = this.url) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.validateUrl(url) === false) {
                this.handlers.close({
                    code: 4000
                });
                return Promise.resolve(false);
            }
            if (this.socket && this.diffUrl(url)) {
                yield this.deactivate();
            }
            if (typeof this.socket === "undefined") {
                const wsURL = this.composeWebsocketUrl(url);
                this.socket = this.constructWS(wsURL);
                this.url = {
                    baseUrl: url.baseUrl,
                    queryParams: {
                        jwt: url.queryParams.jwt,
                        locale: url.queryParams.locale
                    }
                };
            }
            yield this.socket.open();
            return Promise.resolve(this);
        });
    }
    composeWebsocketUrl(url) {
        let wsUrl;
        // TODO: If we use another websocket mock remove this hack
        /* istanbul ignore next */
        if (process.env.NODE_ENV === "test") {
            // jest-websocket-mock doesn't work properly if there are query strings in URL
            wsUrl = url.baseUrl;
        }
        else {
            wsUrl = `${url.baseUrl}${Object.keys(url.queryParams).length > 0 ? "?" : ""}${stringify(url.queryParams)}`;
        }
        return wsUrl;
    }
    diffUrl(url) {
        return url.baseUrl !== this.url.baseUrl || Object.keys(url.queryParams).some(q => {
            return url.queryParams[q] !== this.url.queryParams[q];
        });
    }
    constructWS(url) {
        const socket = new WebSocketAsPromised(url, {
            unpackMessage: data => ({
                data
            })
        });
        this.addListeners(socket);
        return socket;
    }
    addListeners(socket) {
        socket.onOpen.addListener(this.open);
        socket.onError.addListener(this.error);
        socket.onClose.addListener(this.close);
        socket.onUnpackedMessage.addListener(this.message);
    }
    updateToken(token) {
        this.url.queryParams.jwt = token;
    }
    validateUrl(url) {
        return url.baseUrl.startsWith("wss://") && typeof url.queryParams.jwt !== "undefined";
    }
}
//# sourceMappingURL=AsyncSocket.js.map